import { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth'
import { doc, updateDoc, deleteDoc } from 'firebase/firestore'
import { auth, db } from '../../lib/firebase'
import { User, Mail, Lock, Trash2, AlertCircle, CheckCircle, Bell } from 'lucide-react'

export default function Settings() {
  const { currentUser, userProfile, logout, fetchUserProfile } = useAuth()
  const navigate = useNavigate()
  
  const [name, setName] = useState(userProfile?.name || '')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [notifications, setNotifications] = useState(true)
  
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  async function updateProfileInfo(e) {
    e.preventDefault()
    setLoading(true)
    setMessage({ type: '', text: '' })

    try {
      // Update Firebase Auth profile
      await updateProfile(currentUser, { displayName: name })
      
      // Update Firestore
      await updateDoc(doc(db, 'users', currentUser.uid), { name })
      
      await fetchUserProfile(currentUser.uid)
      setMessage({ type: 'success', text: 'Profile updated successfully!' })
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  async function changePassword(e) {
    e.preventDefault()
    
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match!' })
      return
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters!' })
      return
    }

    setLoading(true)
    setMessage({ type: '', text: '' })

    try {
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword)
      await reauthenticateWithCredential(currentUser, credential)
      
      // Update password
      await updatePassword(currentUser, newPassword)
      
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setMessage({ type: 'success', text: 'Password changed successfully!' })
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        setMessage({ type: 'error', text: 'Current password is incorrect!' })
      } else {
        setMessage({ type: 'error', text: 'Failed to change password. Please try again.' })
      }
    } finally {
      setLoading(false)
    }
  }

  async function deleteAccount() {
    if (!window.confirm('Are you absolutely sure? This action cannot be undone.')) {
      return
    }

    setLoading(true)
    try {
      // Delete Firestore documents
      await deleteDoc(doc(db, 'users', currentUser.uid))
      await deleteDoc(doc(db, 'vcards', currentUser.uid))
      await deleteDoc(doc(db, 'analytics', currentUser.uid))
      
      // Delete Firebase Auth account
      await currentUser.delete()
      
      navigate('/')
    } catch (error) {
      if (error.code === 'auth/requires-recent-login') {
        setMessage({ 
          type: 'error', 
          text: 'Please log out and log back in before deleting your account.' 
        })
      } else {
        setMessage({ type: 'error', text: 'Failed to delete account. Please try again.' })
      }
    } finally {
      setLoading(false)
      setShowDeleteModal(false)
    }
  }

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Manage your account settings</p>
      </div>

      {message.text && (
        <div className={`alert ${message.type === 'error' ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: 'var(--space-lg)' }}>
          {message.type === 'error' ? <AlertCircle size={20} /> : <CheckCircle size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      <div className="settings-grid">
        {/* Profile Settings */}
        <div className="dashboard-card">
          <h3 className="dashboard-card-title">
            <User size={20} /> Profile Information
          </h3>
          <form onSubmit={updateProfileInfo}>
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={currentUser?.email || ''}
                disabled
              />
              <span className="form-hint">Email cannot be changed</span>
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner"></span> : 'Save Changes'}
            </button>
          </form>
        </div>

        {/* Password Settings */}
        <div className="dashboard-card">
          <h3 className="dashboard-card-title">
            <Lock size={20} /> Change Password
          </h3>
          {currentUser?.providerData[0]?.providerId === 'password' ? (
            <form onSubmit={changePassword}>
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? <span className="spinner"></span> : 'Change Password'}
              </button>
            </form>
          ) : (
            <p className="text-muted">
              You signed in with Google. Password changes are managed through your Google account.
            </p>
          )}
        </div>

        {/* Notifications */}
        <div className="dashboard-card">
          <h3 className="dashboard-card-title">
            <Bell size={20} /> Notifications
          </h3>
          <div className="notification-option">
            <div>
              <h4>Email Notifications</h4>
              <p>Receive updates about your vCard analytics and tips</p>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                checked={notifications}
                onChange={(e) => setNotifications(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="dashboard-card danger-zone">
          <h3 className="dashboard-card-title">
            <Trash2 size={20} /> Danger Zone
          </h3>
          <p className="text-muted" style={{ marginBottom: 'var(--space-md)' }}>
            Once you delete your account, there is no going back. Please be certain.
          </p>
          <button 
            className="btn btn-danger"
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash2 size={18} />
            Delete Account
          </button>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Account</h3>
            <p>
              This will permanently delete your account, vCard, and all associated data. 
              This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={deleteAccount} disabled={loading}>
                {loading ? <span className="spinner"></span> : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .settings-grid {
          display: grid;
          gap: var(--space-lg);
          max-width: 600px;
        }
        
        .dashboard-card-title {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
        }
        
        .form-hint {
          display: block;
          margin-top: 4px;
          font-size: var(--font-size-xs);
          color: var(--text-muted);
        }
        
        .notification-option {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .notification-option h4 {
          font-size: var(--font-size-base);
          margin-bottom: 4px;
        }
        
        .notification-option p {
          font-size: var(--font-size-sm);
          color: var(--text-muted);
        }
        
        .toggle {
          position: relative;
          width: 48px;
          height: 26px;
        }
        
        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: var(--gray-300);
          transition: .3s;
          border-radius: var(--radius-full);
        }
        
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 20px;
          width: 20px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: .3s;
          border-radius: 50%;
        }
        
        .toggle input:checked + .toggle-slider {
          background-color: var(--primary);
        }
        
        .toggle input:checked + .toggle-slider:before {
          transform: translateX(22px);
        }
        
        .danger-zone {
          border-color: var(--error);
        }
        
        .danger-zone .dashboard-card-title {
          color: var(--error);
        }
        
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: var(--space-lg);
        }
        
        .modal {
          background: white;
          border-radius: var(--radius-xl);
          padding: var(--space-xl);
          max-width: 400px;
          width: 100%;
        }
        
        .modal h3 {
          font-size: var(--font-size-xl);
          margin-bottom: var(--space-md);
        }
        
        .modal p {
          color: var(--text-muted);
          margin-bottom: var(--space-lg);
        }
        
        .modal-actions {
          display: flex;
          gap: var(--space-md);
          justify-content: flex-end;
        }
      `}</style>
    </div>
  )
}
